/**
 * UbicacionRepository - Repositorio unificado para Ubicaciones
 *
 * Implementación compartida entre web (Dexie) y mobile (SQLite).
 * Contiene toda la lógica de negocio de ubicaciones, delegando las operaciones
 * de persistencia al IDatabaseAdapter específico de cada plataforma.
 */

import { v4 as uuidv4 } from 'uuid';
import { IUbicacionRepository } from '../interfaces/IUbicacionRepository';
import { BaseRepository } from './BaseRepository';
import { IDatabaseAdapter, WhereClause } from '../adapters/IDatabaseAdapter';
import { IProfileRepository } from '../interfaces/IProfileRepository';
import { Ubicacion } from '../interfaces/entities';

/**
 * Repositorio unificado de Ubicaciones
 *
 * Este repositorio funciona tanto en web (con DexieAdapter) como en mobile (con SQLiteAdapter).
 *
 * Soft-delete: las ubicaciones NO se borran físicamente; se marca `is_deleted=true`
 * (UPDATE) para que el catch-up incremental las propague a todos los clientes
 * (online u offline). Las lecturas filtran `is_deleted=false`.
 */
export class UbicacionRepository extends BaseRepository<Ubicacion, string> implements IUbicacionRepository {

  /** Clause reutilizable para excluir soft-deletes (is_deleted=false). */
  private readonly NOT_DELETED: WhereClause = { field: 'is_deleted', operator: 'equals', value: false };

  constructor(db: IDatabaseAdapter, profileRepo?: IProfileRepository) {
    super(db, 'ubicaciones', 'ubicaciones'); // tabla local, tabla Supabase

    // Habilitar soft-delete: getAll() filtra is_deleted=false + métodos heredados.
    this.supportsSoftDelete = true;

    if (profileRepo) {
      this.setProfileRepository(profileRepo);
    }
  }

  // ==================== CRUD Básico ====================

  async add(data: Omit<Ubicacion, 'id'>): Promise<string> {
    const id = uuidv4();
    const now = this.getCurrentTimestamp();
    const profile = await this.getCurrentProfile();

    const record: Ubicacion = {
      ...data,
      id,
      created_at: data.created_at || now,
      empresa_id: data.empresa_id || profile?.empresa_activa_id || '',
      is_deleted: false
    } as Ubicacion;

    await this.db.insert('ubicaciones', record);
    this.emitSync('INSERT', record);

    return id;
  }

  /**
   * Override de getAll(): excluye is_deleted=true con filtro falsy
   * (`is_deleted !== true`). Igual que AnimalRepository: filas creadas
   * localmente pueden tener `is_deleted=undefined` (legacy) y deben seguir
   * visibles; sólo se ocultan las marcadas true.
   */
  async getAll(): Promise<Ubicacion[]> {
    const all = await this.db.getAll<Ubicacion>('ubicaciones');
    return all.filter(u => !(u as any)?.is_deleted);
  }

  async update(data: Partial<Ubicacion> & { id: string }): Promise<void> {
    await this.db.update('ubicaciones', data.id, data);
    this.emitSync('UPDATE', data as any);
  }

  async delete(id: string): Promise<void> {
    // Soft-delete: marca is_deleted=true (UPDATE) en vez de borrar físicamente.
    // softDelete setea updated_at + emite SOFT_DELETE→UPDATE (para sync).
    await this.softDelete(id);
  }

  // ==================== Queries Específicas ====================

  async getByUnidadId(unidadId: number): Promise<Ubicacion[]> {
    return this.db.queryMultiple<Ubicacion>('ubicaciones', [
      this.NOT_DELETED,
      { field: 'unidad_id', operator: 'equals', value: unidadId }
    ]);
  }

  async getByTipo(tipo: string): Promise<Ubicacion[]> {
    return this.db.queryMultiple<Ubicacion>('ubicaciones', [
      this.NOT_DELETED,
      { field: 'tipo', operator: 'equals', value: tipo }
    ]);
  }

  async getByEmpresaId(empresaId: string): Promise<Ubicacion[]> {
    return this.db.queryMultiple<Ubicacion>('ubicaciones', [
      this.NOT_DELETED,
      { field: 'empresa_id', operator: 'equals', value: empresaId }
    ]);
  }

  async searchByNombre(query: string): Promise<Ubicacion[]> {
    return this.db.queryMultiple<Ubicacion>('ubicaciones', [
      this.NOT_DELETED,
      { field: 'nombre', operator: 'includes', value: query.toLowerCase() }
    ]);
  }

  // ==================== Bulk Operations ====================

  async bulkAdd(items: Ubicacion[]): Promise<void> {
    await this.db.bulkInsert('ubicaciones', items);

    // Un único evento BULK_INSERT (en lugar de N INSERT) para que SyncManager
    // haga una sola operación bulk en Supabase.
    this.emitBulkSync('BULK_INSERT', items);
  }

  async bulkAddSilent(items: Ubicacion[]): Promise<void> {
    await this.db.bulkInsert('ubicaciones', items);

    items.forEach(item => {
      this.emitLocalSync('INSERT', item);
    });
  }

  async bulkPut(items: Ubicacion[]): Promise<void> {
    await this.db.bulkUpsert('ubicaciones', items);

    // Upsert -> un único evento BULK_INSERT para operación bulk en Supabase.
    this.emitBulkSync('BULK_INSERT', items);
  }

  async bulkPutSilent(items: Ubicacion[]): Promise<void> {
    await this.db.bulkUpsert('ubicaciones', items);

    items.forEach(item => {
      this.emitLocalSync('UPDATE', item);
    });
  }

  /**
   * Bulk SOFT-delete: marca is_deleted=true (UPDATE) para cada id, en lugar de
   * borrar físicamente. Delega en `bulkUpdate` (bulkUpdate local + un único
   * evento BULK_UPDATE → upsert en Supabase llevando is_deleted=true).
   * Emite además eventos locales UPDATE por item para refrescar la UI.
   *
   * Así la cascada de UnidadDetailPage (`ubicacion.bulkDelete(...)`) se vuelve
   * soft automáticamente.
   */
  async bulkDelete(ids: string[]): Promise<void> {
    if (ids.length === 0) return;
    const now = this.getCurrentTimestamp();
    const updates = ids.map(id => ({ id, is_deleted: true, updated_at: now }));
    await this.bulkUpdate(updates as any);
    updates.forEach(u => this.emitLocalSync('UPDATE', u as any));
  }

  async bulkDeleteSilent(ids: string[]): Promise<void> {
    await this.db.bulkDelete('ubicaciones', ids);

    ids.forEach(id => {
      this.emitLocalSync('DELETE', id);
    });
  }

  // ==================== Overrides de BaseRepository ====================

  /**
   * Obtiene la última ubicación por fecha de creación
   */
  async getLastByDate(dateColumn: string = 'created_at'): Promise<Ubicacion | undefined> {
    const results = await this.db.query<Ubicacion>('ubicaciones', undefined, {
      column: dateColumn,
      direction: 'desc'
    });

    return results[0] || undefined;
  }
}
